import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { groupCourseTestsByTab } from "../pages/Enlaces_Administracion/Acciones_gc/Cursos_Creacion/groupCourseTestsByTab";
import { moveCourseTestToIndex } from "../utils/courseTestsReorder";
import { collectLinkedTestTitles } from "../utils/temarioTree";
import { getMessage } from "../data/locales";

function linkedTitlesFromContenido(contenido) {
  const titles = new Set();
  if (!contenido || typeof contenido !== "object") {
    return titles;
  }
  for (const nodes of Object.values(contenido)) {
    if (!Array.isArray(nodes)) {
      continue;
    }
    for (const title of collectLinkedTestTitles(nodes)) {
      titles.add(title);
    }
  }
  return titles;
}

function testTitle(test) {
  return typeof test?.titulo === "string" ? test.titulo : "";
}

function unlinkedItems(items, linkedTitles) {
  return items.filter((entry) => !linkedTitles.has(testTitle(entry.test)));
}

/**
 * Documents-tab test groups. Tests already linked to a node are ordered in
 * that node's list, so only unlinked tests keep reorder arrows here.
 */
export default function AdminDocsTestsByTab({
  tests,
  pestanasCurso,
  contenido,
  onTestsChange,
  disabled,
}) {
  const grouped = useMemo(
    () => groupCourseTestsByTab(tests, pestanasCurso),
    [tests, pestanasCurso],
  );
  const linkedTitles = useMemo(() => linkedTitlesFromContenido(contenido), [contenido]);
  const list = Array.isArray(tests) ? tests : [];

  const moveToIndex = (index, targetIndex) => {
    if (disabled || typeof onTestsChange !== "function") {
      return;
    }
    const next = moveCourseTestToIndex(list, index, targetIndex);
    if (next) {
      onTestsChange(next);
    }
  };

  const hasVisibleGroup = grouped.bloquesOrdenados.some(
    (bloque) => (grouped.porBloque[bloque] || []).length > 0,
  );
  if (!hasVisibleGroup) {
    return null;
  }

  return (
    <section className="cc-docs-tests-by-tab" aria-labelledby="editar-docs-tests-heading">
      <h2 id="editar-docs-tests-heading" className="cc-docs-section-heading">
        {getMessage("courseEditor.testsTemario.docsTestsByTabHeading")}
      </h2>
      <p className="cc-docs-section-text">
        {getMessage("courseEditor.testsTemario.docsTestsByTabHint")}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {grouped.bloquesOrdenados.map((bloque) => {
          const items = grouped.porBloque[bloque] || [];
          if (items.length === 0) {
            return null;
          }
          const movable = unlinkedItems(items, linkedTitles);
          return (
            <div
              key={bloque}
              className="admin-docs-tests-group"
              data-testid={`admin-docs-tests-group-${bloque}`}
              style={{
                border: "1px solid #17a398",
                borderRadius: 8,
                overflow: "hidden",
                backgroundColor: "rgba(13, 27, 42, 0.6)",
              }}
            >
              <div
                className="admin-docs-tests-group-header"
                style={{
                  padding: "10px 16px",
                  backgroundColor: "rgba(23, 163, 152, 0.2)",
                  fontWeight: "bold",
                  fontSize: 15,
                }}
              >
                {`📁 ${bloque}`}
              </div>
              <ul style={{ listStyle: "none", padding: "12px 16px", margin: 0 }}>
                {items.map(({ test, index }) => {
                  const titulo = testTitle(test);
                  const linked = linkedTitles.has(titulo);
                  const movableIndex = movable.findIndex((entry) => entry.index === index);
                  const previous = movableIndex > 0 ? movable[movableIndex - 1] : null;
                  const nextItem = movableIndex >= 0 && movableIndex < movable.length - 1
                    ? movable[movableIndex + 1]
                    : null;
                  return (
                    <li
                      key={`${bloque}-${titulo || "test"}-${index}`}
                      className="temario-content-resource-row temario-content-resource-row--test"
                    >
                      <span className="temario-content-resource-name temario-content-resource-name--test">
                        {getMessage("topic.testResourceLabel", { title: titulo })}
                      </span>
                      {linked ? null : (
                        <>
                          <button
                            type="button"
                            disabled={disabled || !previous}
                            aria-label={getMessage("courseEditor.testsTemario.moveTestUp", { name: titulo })}
                            title={getMessage("courseEditor.testsTemario.moveTestUp", { name: titulo })}
                            onClick={() => {
                              if (previous) {
                                moveToIndex(index, previous.index);
                              }
                            }}
                          >
                            {getMessage("courseEditor.testsTemario.moveTestUpLabel")}
                          </button>
                          <button
                            type="button"
                            disabled={disabled || !nextItem}
                            aria-label={getMessage("courseEditor.testsTemario.moveTestDown", { name: titulo })}
                            title={getMessage("courseEditor.testsTemario.moveTestDown", { name: titulo })}
                            onClick={() => {
                              if (nextItem) {
                                moveToIndex(index, nextItem.index);
                              }
                            }}
                          >
                            {getMessage("courseEditor.testsTemario.moveTestDownLabel")}
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

AdminDocsTestsByTab.propTypes = {
  tests: PropTypes.arrayOf(PropTypes.object),
  pestanasCurso: PropTypes.arrayOf(PropTypes.string),
  contenido: PropTypes.objectOf(PropTypes.array),
  onTestsChange: PropTypes.func,
  disabled: PropTypes.bool,
};

AdminDocsTestsByTab.defaultProps = {
  tests: [],
  pestanasCurso: [],
  contenido: null,
  onTestsChange: undefined,
  disabled: false,
};
